import React, {useEffect, useMemo, useCallback, useState, useRef} from 'react';
import { TabView, Tab } from '@react/react-spectrum/TabView';
import {Divider, Flex} from '@adobe/react-spectrum'


import styled from 'styled-components';

import WidgetTab from 'views/widgetTab';
import ExceptionsTab from 'views/exceptionsTab';
import ExtensionsTab from 'views/extensionsTab';
import AlertsTab from "views/alertsTab";
import PatchesTab from 'views/patchesTab';
import InformationTab from 'views/informationTab';
import RecommendationsTab from 'views/recommendationsTab';
import CapsTab from 'views/capsTab';
import SplashPage from 'views/splashPage';
import MaintenancePage from 'views/maintenancePage';
import UnauthorizedPage from 'views/unauthorizedPage';
import Header from './components/header';
import Footer from './components/footer';
import Loader from './components/loader';
import SecurityEnforcementDialog from './components/securityEnforcementDialog';

import ClickableTooltip from "views/base/components/clickableTooltip";
import useDashboard from "state/dashboard/hooks/useDashboard";
import useContextHelp from "state/contextHelp/hooks/useContextHelp";
import {useHistory, useLocation} from "react-router-dom";
import { useParams } from 'react-router';
import HelpOutline from "@spectrum-icons/workflow/HelpOutline";
import useAccess from "state/dashboard/hooks/useAccess";
import useClientAccess from "state/dashboard/hooks/useClientAccess";
import ImsErrorPage from "./components/imsError";
import Gainsight from "./components/gainsight";
import CommerceServicesTab from "../commerceServicesTab";
import CloudReportsTab from "../cloudReportsTab";

const Main = styled.main`
  padding: 0 40px;
  background-color:#F5F5F5;
`;

const Base = () => {
  const location = useLocation();
  let history = useHistory();
  const { context, tab } = useParams();
  const hasRole = useAccess();
  const hasClientAccess = useClientAccess();
  const [,, requestContextHelp] = useContextHelp();
  const [dashboard, isLoading, getDashboardData] = useDashboard();

  useEffect(() => {
      getDashboardData(location.search, requestContextHelp);
    }, [dashboard.displaySplashPage, dashboard.displayMaintenancePage, dashboard.displayUnauthorizedPage]
  );

  // Security Enforcement Policy pop-up: opens once per *change* of the currently loaded
  // project (covers both a customer's single login and an internal user switching projects
  // via the header picker, since both paths reload the dashboard the same way). Reselecting
  // the same project is a no-op; navigating away and back re-opens it if still flagged.
  const [isSecurityEnforcementDialogOpen, setIsSecurityEnforcementDialogOpen] = useState(false);
  const lastSecurityEnforcementClientIdRef = useRef(undefined);
  useEffect(() => {
    const currentClientId = dashboard.client.id;
    // '' is the INITIAL_STATE sentinel for "no client loaded yet" -- not a real id, but a real
    // client id could legitimately be falsy (e.g. 0), so check for the sentinel explicitly rather
    // than relying on truthiness.
    if (currentClientId === '' || currentClientId == null) {
      return;
    }
    if (currentClientId !== lastSecurityEnforcementClientIdRef.current) {
      if (dashboard.securityEnforcementAlert) {
        setIsSecurityEnforcementDialogOpen(true);
      }
      lastSecurityEnforcementClientIdRef.current = currentClientId;
    }
  }, [dashboard.client.id, dashboard.securityEnforcementAlert]);

  // Memoize helper functions
  const isVisible = useCallback((name) => {
    return dashboard.menu && dashboard.menu.hasOwnProperty(name);
  }, [dashboard.menu]);

  const isSelected = useCallback((index) => {
    return index === tab || !tab && index === 'main';
  }, [tab]);

  // Build dynamic tab ID mapping based on which tabs are actually visible
  const visibleTabIds = useMemo(() => {
    const tabs = [
      { id: 'main', condition: true },
      { id: 'information', condition: true },
      { id: 'recommendations', condition: true },
      { id: 'exceptions', condition: true },
      { id: 'extensions', condition: true },
      { id: 'alerts', condition: hasClientAccess('magento-commerce-cloud') },
      { id: 'patches', condition: true },
      { id: 'commerceServices', condition: isVisible("commerce_services") },
      { id: 'cloudReportsTab', condition: hasRole("internalUser") },
      { id: 'patchingAutomation', condition: hasClientAccess('magento-commerce-cloud') }
    ];
    
    return tabs.filter(tab => tab.condition).map(tab => tab.id);
  }, [hasClientAccess, hasRole, isVisible]);

  const onSelect = useCallback((tabId) => {
    if (visibleTabIds[tabId]) {
      history.push('/commerce/tab/' + visibleTabIds[tabId]);
    }
  }, [history, visibleTabIds]);

  const LabelWithHelp = useCallback(({title, code}) => {
    return <span>
      {title}
      <ClickableTooltip
        color="#6e6e6e"
        margin="0 10px"
        code={code}
      >
        <HelpOutline size="XS"/>
      </ClickableTooltip>
    </span>
  }, []);

  // Side effects and redirects
  if (context) {
    sessionStorage.setItem('context', context);
  }
  let redirectToAfterAuth = localStorage.getItem('redirectToAfterAuth')
  if (redirectToAfterAuth && history.location.pathname !== redirectToAfterAuth) {
    localStorage.removeItem('redirectToAfterAuth')
    history.push(redirectToAfterAuth);
  }

  // Early returns for loading and error states (after all hooks)
  if (isLoading) {
    return <Loader/>;
  }
  if (dashboard.displayUnauthorizedPage) {
    return <UnauthorizedPage/>;
  }
  if (dashboard.displayMaintenancePage) {
    return <MaintenancePage/>;
  }
  if (dashboard.displaySplashPage) {
    return <SplashPage/>;
  }
  if (dashboard.imsErrorCode !== '00000') {
    return <ImsErrorPage/>;
  }

  return <>
      <Flex direction={'column'} gap={'size-125'} UNSAFE_style={{backgroundColor: '#F5F5F5'}}>
        <Header />
        <Divider size={'M'}/>
        <Main>
          {/*
            TabView (@react/react-spectrum v2) only updates its visible panel from its own click
            handling; it doesn't resync when a Tab's `selected` prop changes from an external
            history.push (e.g. the security enforcement dialog's "View Recommendations" CTA
            navigating in from a different tab). Ordinary same-component clicks never hit this --
            they never carry `selectIssueCode` -- so this key stays 'stable' and never remounts on
            normal navigation; it only takes on a fresh value (forcing exactly one resync) for the
            one external-navigation case, then reverts to 'stable' on the next ordinary click.
          */}
          <TabView key={location.state?.selectIssueCode ? location.key : 'stable'} className="mainTabNav" onSelect={onSelect}>
            <Tab selected={isSelected('main')} label={<LabelWithHelp title="Dashboard" code="helpWidget"/>} data-ui-id="main-tab">
              <WidgetTab />
            </Tab>
            <Tab selected={isSelected('information')} label={<LabelWithHelp title="Information" code="helpInformation"/>} data-ui-id="information-tab">
              <InformationTab />
            </Tab>
            <Tab selected={isSelected('recommendations')} label={<LabelWithHelp title={"Recommendations (" + dashboard.counts.recommendations + ")"} code="helpRecommendations"/>} data-ui-id="recommendations-tab">
              <RecommendationsTab />
            </Tab>
            <Tab selected={isSelected('exceptions')} label={<LabelWithHelp title={"Exceptions (" + dashboard.counts.exceptions + ")"} code="helpExceptions"/>} data-ui-id="exceptions-tab">
              <ExceptionsTab />
            </Tab>
            <Tab selected={isSelected('extensions')} label={<LabelWithHelp title={"Extensions (" + dashboard.counts.extensions + ")"} code="helpExtensions"/>} data-ui-id="extensions-tab">
              <ExtensionsTab />
            </Tab>
            {
              hasClientAccess('magento-commerce-cloud') && <Tab selected={isSelected('alerts')} label={<LabelWithHelp title={"Alerts (" + dashboard.counts.alerts + ")"} code="helpAlerts"/>} data-ui-id="alerts-tab">
                <AlertsTab />
              </Tab>
            }
            <Tab selected={isSelected('patches')} label={<LabelWithHelp title={"Patches (" + dashboard.counts.patches + ")"} code="helpPatches"/>} data-ui-id="patches-tab">
              <PatchesTab />
            </Tab>
            {
              isVisible("commerce_services") && <Tab selected={isSelected('commerceServices')} label={<LabelWithHelp title={"Commerce Services"} code="helpCommerceServices"/>}>
                <CommerceServicesTab />
              </Tab>
            }
            {
              hasRole("internalUser")  && <Tab selected={isSelected('cloudReportsTab')} label={<LabelWithHelp title={"Pre-Launch Checklist"} code="cloudReportsTab"/>}>
                <CloudReportsTab />
              </Tab>
            }
            {
              hasClientAccess('magento-commerce-cloud') && <Tab selected={isSelected('patchingAutomation')} label={<LabelWithHelp title={"Patching Automation"} code="helpCaps"/>}>
                <CapsTab />
              </Tab>
            }
          </TabView>
        </Main>
      </Flex>
      <Footer />
      <Gainsight/>
      <SecurityEnforcementDialog
        isOpen={isSecurityEnforcementDialogOpen}
        onClose={() => setIsSecurityEnforcementDialogOpen(false)}
      />
    </>
};

export default Base;
